<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  teams: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['create']);
const { t } = useI18n();
const dialogRef = ref(null);
const form = reactive({ email: '', teamId: '' });
const touched = ref(false);

const emailIsValid = computed(() =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
);
const isFormInvalid = computed(() => !emailIsValid.value);
const emailMessage = computed(() =>
  touched.value && !emailIsValid.value
    ? t('INBOX_MGMT.EMAIL_LIST.CREATE.EMAIL_ERROR')
    : ''
);
const teamOptions = computed(() => [
  {
    value: '',
    label: t('INBOX_MGMT.EMAIL_LIST.CREATE.NO_TEAM'),
  },
  ...props.teams.map(team => ({ value: team.id, label: team.name })),
]);

const resetForm = () => {
  form.email = '';
  form.teamId = '';
  touched.value = false;
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const onSuccess = () => {
  close();
  resetForm();
};

const handleConfirm = () => {
  touched.value = true;
  if (isFormInvalid.value) return;

  emit('create', {
    email: form.email.trim().toLowerCase(),
    teamId: form.teamId || null,
  });
};

defineExpose({ open, onSuccess });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('INBOX_MGMT.EMAIL_LIST.CREATE.TITLE')"
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-5">
      <Input
        v-model="form.email"
        type="email"
        :label="t('INBOX_MGMT.EMAIL_LIST.CREATE.EMAIL_LABEL')"
        :placeholder="t('INBOX_MGMT.EMAIL_LIST.CREATE.EMAIL_PLACEHOLDER')"
        :message="emailMessage"
        :message-type="emailMessage ? 'error' : 'info'"
        :disabled="isLoading"
        autocomplete="email"
        autofocus
        @blur="touched = true"
        @enter="handleConfirm"
      />

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('INBOX_MGMT.EMAIL_LIST.CREATE.TEAM_LABEL') }}
        </label>
        <ComboBox
          v-model="form.teamId"
          :options="teamOptions"
          :placeholder="t('INBOX_MGMT.EMAIL_LIST.CREATE.NO_TEAM')"
          :search-placeholder="t('INBOX_MGMT.EMAIL_LIST.CREATE.SEARCH_TEAMS')"
          :disabled="isLoading"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          color="slate"
          type="button"
          @click="close"
        />
        <Button
          :label="t('INBOX_MGMT.EMAIL_LIST.CREATE.ACTION')"
          type="submit"
          :disabled="isFormInvalid || isLoading"
          :is-loading="isLoading"
        />
      </div>
    </template>
  </Dialog>
</template>
